import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import { guidePages } from "../data/guidePages";
import { businessInfo } from "../mock";
import { localBusinessSchema, breadcrumbSchema } from "../utils/schemas";

const GuidesHubPage = () => {
  const guides = Object.values(guidePages);
  const schemas = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/guides" },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title="Florida Public Adjuster Guides | Claim Beast"
        description="Free guides to help Florida homeowners navigate an insurance claim — public adjuster vs. insurance adjuster, whether you need one, cost, and public adjuster vs. attorney. Straight answers from a licensed Tampa Bay public adjuster."
        path="/guides"
        keywords="public adjuster guide florida, insurance claim guide florida, do i need a public adjuster, public adjuster cost florida"
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={[{ name: "Guides", path: "/guides" }]} />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Homeowner Guides
            </span>
          </div>
          <h1
            className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Florida Claim <span className="text-[#E11D26]">Guides</span>
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            Straight answers to the questions Florida homeowners ask before
            hiring help with an insurance claim. No jargon, no sales pitch —
            just what you need to make the right call for your home.
          </p>
        </div>
      </section>

      {/* Guides grid */}
      <section className="py-14 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-5">
            {guides.map((g) => (
              <Link
                key={g.slug}
                to={`/${g.slug}`}
                data-testid={`guide-hub-card-${g.slug}`}
                className="group flex flex-col justify-between bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-7 transition-colors"
              >
                <div>
                  <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                    {g.eyebrow}
                  </span>
                  <h2
                    className="mt-3 text-white font-black uppercase text-xl sm:text-2xl tracking-wide leading-tight group-hover:text-[#E11D26] transition-colors"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {g.h1}
                  </h2>
                  <p className="mt-3 text-white/70 text-sm leading-relaxed">
                    {g.intro.slice(0, 160)}…
                  </p>
                </div>
                <span className="mt-5 inline-flex items-center gap-2 text-white/80 text-sm font-bold tracking-wider uppercase">
                  Read Guide
                  <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Northwest Florida hurricane hubs */}
      <section className="py-14 bg-black border-t border-white/10" data-testid="guides-nwfl-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
            Hurricane Isaias · Northwest Florida
          </span>
          <h2
            className="mt-3 text-white text-2xl sm:text-3xl font-black uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Panhandle Hurricane Claim Guides
          </h2>
          <p className="mt-3 text-white/70 text-sm sm:text-base max-w-3xl leading-relaxed">
            Hurricane Isaias made landfall near Destin on October 9, 2026 as a Category 2
            hurricane. These location-specific hubs cover roof, wind, water, storm surge,
            denied, and underpaid claims for Escambia, Santa Rosa, Okaloosa, and Walton counties.
          </p>
          <div className="mt-7 grid md:grid-cols-2 gap-5">
            {[
              { to: "/hurricane-isaias-pensacola", eyebrow: "Pensacola · Escambia · Santa Rosa", title: "Hurricane Isaias Claim Help in Pensacola", blurb: "Western-eyewall winds above 100 mph near Santa Rosa Sound and Pensacola. Roof, wind-driven rain, surge, denied and underpaid claims." },
              { to: "/hurricane-isaias-destin", eyebrow: "Destin · Okaloosa · Walton", title: "Hurricane Isaias Claim Help in Destin", blurb: "Landfall point. Condo master-policy vs. HO-6, vacation-rental income, Gulf-front surge, metal and tile roof disputes." },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`guide-hub-nwfl-${c.to.replace(/\//g, "")}`}
                className="group flex flex-col justify-between bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-7 transition-colors"
              >
                <div>
                  <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">{c.eyebrow}</span>
                  <h3
                    className="mt-3 text-white font-black uppercase text-xl sm:text-2xl tracking-wide leading-tight group-hover:text-[#E11D26] transition-colors"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {c.title}
                  </h3>
                  <p className="mt-3 text-white/70 text-sm leading-relaxed">{c.blurb}</p>
                </div>
                <span className="mt-5 inline-flex items-center gap-2 text-white/80 text-sm font-bold tracking-wider uppercase">
                  View Hub
                  <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Ready to talk about your claim?
          </h3>
          <a
            href={`tel:${businessInfo.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <Phone className="h-4 w-4" /> {businessInfo.phone}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidesHubPage;
